import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker, PaginatedResponse, processManualContribution } from '../api/worker';
import { Transaction, SplitResult, Contribution, CreatePaymentIntentRequest } from '../types';

/**
 * Fetch paginated ledger transactions
 */
export const useLedgerTransactions = (nodeId: string, includeSubnodes: boolean, page: number, size: number = 15) => {
  return useQuery({
    queryKey: ['ledger-transactions', nodeId, includeSubnodes, page, size],
    queryFn: async () => {
      const response = await apiWorker.get<PaginatedResponse<Transaction>>(
        `/api/ledger/${nodeId}/transactions?page=${page}&size=${size}${includeSubnodes ? '&includeSubnodes=true' : ''}`
      );
      return response.data;
    },
    enabled: !!nodeId
  });
};

/**
 * Fetch high-level ledger statistics for an admin
 */
export const useLedgerStats = (nodeId: string, includeSubnodes: boolean, isAdmin: boolean) => {
  return useQuery({
    queryKey: ['ledger-stats', nodeId, includeSubnodes],
    queryFn: async () => {
      const response = await apiWorker.get(
        `/api/ledger/${nodeId}/stats${includeSubnodes ? '?includeSubnodes=true' : ''}`
      );
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!nodeId && isAdmin
  });
};

/**
 * Fetch personal ledger statistics for a regular member
 */
export const useMyLedgerStats = (nodeId: string, isAdmin: boolean) => {
  return useQuery({
    queryKey: ['ledger-stats-me', nodeId],
    queryFn: async () => {
      const response = await apiWorker.get(`/api/ledger/${nodeId}/stats/me`);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!nodeId && !isAdmin
  });
};

/**
 * Calculate theoretical revenue split for a given amount
 */
export const useCalculateSplit = (nodeId: string, amount: number) => {
  return useQuery<SplitResult[]>({
    queryKey: ['ledger-split', nodeId, amount],
    queryFn: async () => {
      if (!amount || amount <= 0) return [];
      const response = await apiWorker.post(`/api/ledger/${nodeId}/split`, { amount });
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!nodeId && amount > 0
  });
};

/**
 * Record a manual offline contribution (e.g. Cash/Check)
 */
export const useManualContribution = (nodeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { amount: number; description: string }) => 
      processManualContribution(nodeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ledger-transactions', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['ledger-stats', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['ledger-stats-me', nodeId] });
    }
  });
};

/**
 * Fetch financial goals to display as fundraisers
 */
export const useLedgerFundraisers = (nodeId: string) => {
  return useQuery({
    queryKey: ['ledger-fundraisers', nodeId],
    queryFn: async () => {
      const response = await apiWorker.get(`/api/nodes/${nodeId}/goals`);
      const list = Array.isArray(response.data) 
        ? response.data 
        : (response.data && Array.isArray(response.data.content) ? response.data.content : []);
      // Filter for financial goals to display as fundraisers
      return list.filter((g: any) => g.unit === '$');
    },
    enabled: !!nodeId
  });
};

/**
 * Fetch recent node contributions
 */
export const useLedgerContributions = (nodeId: string) => {
  return useQuery<Contribution[]>({
    queryKey: ['contributions', nodeId],
    queryFn: async () => {
      const response = await apiWorker.get(`/api/communications/${nodeId}/contributions`);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!nodeId
  });
};

/**
 * Create a Stripe Payment Intent
 */
export const useCreatePaymentIntent = (nodeId: string) => {
  return useMutation({
    mutationFn: (data: CreatePaymentIntentRequest) => 
      apiWorker.post(`/api/ledger/${nodeId}/charge`, data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
  });
};
